import {
  getPhoneCode,
  isPossiblePhoneNumber,
  isValidNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

export interface SmsOriginCountry {
  code: CountryCode;
  dialCode: string;
  flag: string;
}

export interface SmsOriginNumberResponse {
  name: string;
  alpha2Code: string;
  countryCode: string;
  msisdn: string;
  flag: string;
}

export const mapSmsOriginCountries = (entries: SmsOriginNumberResponse[]): SmsOriginCountry[] =>
  entries
    .filter((e) => !!e.alpha2Code)
    .map((e) => ({
      code: e.alpha2Code as CountryCode,
      dialCode: `+${getPhoneCode(e.alpha2Code as CountryCode)}`,
      flag: e.flag,
    }));

export const getMobileValidation = (value: string, country: CountryCode, allowedCountries: string[]) => {
  if (!value) {
    return { valid: false, error: 'Mobile number is required', incomplete: true };
  }

  if (!allowedCountries.includes(country)) {
    return { valid: false, error: 'Mobile numbers from this country are not supported', incomplete: false };
  }

  const parsedNumber = parsePhoneNumberFromString(value, country);

  if (!parsedNumber) {
    return {
      valid: false,
      error: 'Please enter a valid mobile number',
      incomplete: true,
    };
  }

  if (parsedNumber.country !== country) {
    return {
      valid: false,
      error: 'Please enter a valid mobile number',
      incomplete: false,
    };
  }

  if (!isPossiblePhoneNumber(value, country) || !isValidNumber(value, country)) {
    return {
      valid: false,
      error: 'Please enter a valid mobile number',
      incomplete: false,
    };
  }

  return {
    valid: true,
    error: '',
    incomplete: false,
  };
};

export const isValidMobile = (value: string, country: CountryCode, allowedCountries: string[]) =>
  getMobileValidation(value, country, allowedCountries).valid;

export const parseMobile = (value: string, country: CountryCode) => {
  const parsedNumber = parsePhoneNumberFromString(value, country);

  if (!parsedNumber) {
    return value;
  }

  return parsedNumber.number;
};
